import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaSave, FaArrowLeft, FaEye, FaDownload } from "react-icons/fa";
import { toast } from "react-toastify";
import { PDFBuilder, PDFViewer, downloadPDF } from "@psyfiers-it/ui-lib";

import { FullScreenLoader } from "../components/common";
import { BaseModal } from "../components/popups";
import { getCustomPdfById, updateCustomPdf } from "../service";

export default function CustomPdfEditor() {
  const [pdf, setPdf] = useState(null);
  const [title, setTitle] = useState("");
  const [documentJson, setDocumentJson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { orgId, pdfId } = useParams();
  const navigate = useNavigate();

  const fetchPdf = async () => {
    try {
      setLoading("Loading PDF...");
      const { data } = await getCustomPdfById(orgId, pdfId);
      setPdf(data);
      setTitle(data.title || "");
      setDocumentJson(data.json || null);
    } catch (error) {
      console.error("Fetch PDF error", error);
      toast.error("Failed to load PDF");
      navigate(`/dashboard/organisation/custom-pdfs/${orgId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    try {
      setLoading("Saving PDF...");
      await updateCustomPdf(orgId, pdfId, {
        title,
        json: documentJson
      });
      setHasChanges(false);
      toast.success("PDF saved successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Failed to save PDF");
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentChange = useCallback((newDocument) => {
    setDocumentJson(newDocument);
    setHasChanges(true);
  }, []);

  const handleBack = () => {
    if (hasChanges) {
      if (window.confirm("You have unsaved changes. Are you sure you want to leave?")) {
        navigate(`/dashboard/organisation/custom-pdfs/${orgId}`);
      }
    } else {
      navigate(`/dashboard/organisation/custom-pdfs/${orgId}`);
    }
  };

  const handleDownload = async () => {
    if (!documentJson) {
      toast.error("No PDF content to download");
      return;
    }

    try {
      setLoading("Generating PDF...");
      await downloadPDF(documentJson, title || "custom-pdf");
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Download error", error);
      toast.error("Failed to download PDF");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orgId || !pdfId) return;
    fetchPdf();
  }, [orgId, pdfId]);

  if (!pdf && !loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="h-screen w-full fixed top-0 left-0 z-50 flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-full"
            title="Back to list">
            <FaArrowLeft className="text-gray-600" />
          </button>
          <input
            type="text"
            className="text-xl font-semibold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-2 py-1"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setDocumentJson((prev) => (prev ? { ...prev, title: e.target.value } : null));
              setHasChanges(true);
            }}
            placeholder="PDF Title"
          />
          {hasChanges && <span className="text-sm text-orange-500">(unsaved changes)</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setDocumentJson((prev) => (prev ? { ...prev, title: title } : null));
              setShowPreview(true);
            }}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            title="Preview PDF">
            <FaEye />
            Preview
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            title="Download PDF">
            <FaDownload />
            Download
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            title="Save PDF">
            <FaSave />
            Save
          </button>
        </div>
      </div>

      {/* PDF Builder */}
      <div className="flex-1 overflow-hidden">
        {documentJson && (
          <PDFBuilder
            initialDocument={documentJson}
            onChange={handleDocumentChange}
            // onExportJSON={handleExportJSON}
          />
        )}
      </div>

      {/* Preview Modal */}
      <BaseModal
        open={showPreview}
        setOpen={setShowPreview}
        title="PDF Preview"
        modalSize="w-[90%] max-w-6xl h-[90vh]">
        <div className="h-full">
          {documentJson ? (
            <PDFViewer
              document={documentJson}
              showDownload={false}
              downloadFileName={title || "custom-pdf"}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              No PDF content to preview. Add some elements in the builder.
            </div>
          )}
        </div>
      </BaseModal>

      {loading && <FullScreenLoader text={loading} />}
    </div>
  );
}
